import React, { useEffect, useState, useMemo, memo} from 'react';
import { Calendar, Clock, User, MapPin, CheckCircle, AlertCircle, Loader, ChevronDown, Edit3, MessageCircle, Trash2, Cross, Plus, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Lesson, LessonAction } from '../components/interfaces/All';
import { useSupabase } from '../context/SupabaseContext';

// Intents
// - see the lessons
// see if my day is full with lessons (teacher)

const LessonsList: React.FC = () => {
  const navigate = useNavigate();

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  // const [todayLessons, setTodayLessons] = useState<Lesson[]>([]);
  const { state, fetchLessons, cancelLesson, completeLesson, allTimeslotswOfTheDay, findAvailableSlots } = useSupabase();

  useMemo(() => {
    fetchLessons();
  }, []);

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarOpen && !event.target.closest('.calendar-dropdown')) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [calendarOpen]);

  // NEW: Quick date presets
  const quickDates = [
    { label: 'Yesterday', date: (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })() },
    { label: 'Today', date: new Date() },
    { label: 'Tomorrow', date: (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })() },
  ];

  const getStatusIcon = (status: Lesson['status']) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'pending': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case 'canceled': return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusColor = (status: Lesson['status']) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200';
      case 'pending': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200';
      case 'canceled': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
    }
  };


  const formatDate = (date: Date) => date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // filter todays lessons and sort by time
  const filterTodayLessons = () => {
    const today = selectedDate;
    today.setHours(0, 0, 0, 0); // Start of today

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return state.lessons
      .filter(lesson => {
        const lessonDate = new Date(lesson.time);
        return lessonDate >= today && lessonDate < tomorrow;
      })
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  };
  const filterTodayNonCanceledLessons = () => {
    return filterTodayLessons().filter(lesson => lesson.status !== 'canceled');
  }

  const handleAction = async (lessonId: string, action: LessonAction) => {
    setLoadingIds(prev => new Set([...prev, lessonId]));

    try {
      if (action === 'details') {
        navigate(`/lesson/${lessonId}`);
      }

      if (action === 'canceled') {
        if (!lessonId) return;

        if (window.confirm(`Cancel lesson?`)) {
          try {
            await cancelLesson(lessonId);
          } catch (err) {
            console.error(err);
          } finally {
            fetchLessons();
          }
        }
      }

      if (action === 'completed') {
        await completeLesson(lessonId);
        navigate(`/lesson/${lessonId}/feedback`);
      }

    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      fetchLessons();
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(lessonId);
        return newSet;
      });
    }
  };

  const renderActionButton = (lessonId: string, action: LessonAction, label: string, icon: React.ReactNode, color: string) => (
    <button
      className={`btn text-xs px-3 py-1 flex-1 ${color}`}
      onClick={() => handleAction(lessonId, action)}
      disabled={loadingIds.has(lessonId)}
    >
      {loadingIds.has(lessonId) ? (
        <Loader className="w-3 h-3 animate-spin mr-1" />
      ) : icon}
      <span>{label}</span>
    </button>
  );

  const AvailabilityBar = memo(function AvailabilityBar({ date }: { date: string; }) {
    const allSlots = state.allTimeslotswOfTheDay;
    const takenSlots = filterTodayLessons();
    const availableSlots = allSlots.filter(slot => {
      return !takenSlots.some(lesson => {
        const lessonStart = new Date(lesson.time);
        const lessonEnd = new Date(lessonStart.getTime() + parseInt(lesson.duration) * 60000);
        return (slot.start < lessonEnd && slot.end > lessonStart);
      });
    });

    // Width of bar in “slots”
    const barWidth = allSlots.length;

    return (
      <div className="flex flex-col gap-1 w-full">
        {/* Header */}
        <div className="flex justify-between items-center text-xs text-gray-600">
          <span>Today • {allSlots.length} slots</span>
          <span className="text-sm font-medium text-gray-800">
            {availableSlots.length}/{allSlots.length} available
          </span>
        </div>

        {/* Thin bar */}
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          {Array(barWidth).fill(0).map((_, i) => {
            const slot = allSlots[i];
            const isAvailable = availableSlots.some(
              (a) => a.start === slot.start && a.end === slot.end
            );

            return (
              <div
                key={`${slot.start.getTime()}-${slot.end.getTime()}`}
                className={`h-full flex-1 ${
                  isAvailable
                    ? "bg-yellow-400" // available “sticks out”
                    : "bg-blue-500"
                }`}
              />
            );
          })}
        </div>
      </div>
    );
  });

  return (
    <div className="fade-in max-w-md mx-auto pt-4">
      {/* Header */}
      <div className="mb-8 calendar-dropdown">
        <div className="flex items-center gap-3 mb-2 ps-2 pe-2">
            <button
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all group"
              onClick={() => setCalendarOpen(!calendarOpen)}
            >
              <Calendar className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" />
            </button>

          <div>
            <h1 className="text-xl font-bold">Lessons</h1>
            <p className="text-text-light text-sm">{formatDate(selectedDate)}</p>
          </div>
          {/* button (add new) */}
          <div className="ml-auto">
            <button
              className="btn bg-light shadow-md rounded-full p-2 items-center"
              onClick={() => navigate('/lesson/create')}
            >
              <PlusCircle className="w-6 h-6 mr-0" />
            </button>
          </div>
        </div>

        {/* NEW: Calendar Date Dropdown */}
          {calendarOpen && (
            <div className="**z-[999]** top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-border rounded-2xl shadow-2xl z-50 p-3 max-h-80 overflow-y-auto">
              <div className="space-y-2">
                {/* Quick Presets */}
                <div className="flex gap-2 mb-3">
                  {quickDates.map((preset) => (
                    <button
                      key={preset.label}
                      className="flex items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-left"
                      onClick={() => {
                        setSelectedDate(preset.date);
                        allTimeslotswOfTheDay(new Date(preset.date));
                        findAvailableSlots(new Date(preset.date), new Date(preset.date).setHours(0, 0, 0, 0) );
                        setCalendarOpen(false);
                      }}
                    >
                      {selectedDate.setHours(0, 0, 0, 0) == preset.date.setHours(0, 0, 0, 0) &&
                        <span className="w-2 h-2 rounded-full bg-primary" />
                      }
                      <span className="font-medium">{preset.label}</span>
                    </button>
                  ))}
                </div>

                {/* horizontal availiability indicator */}
                <AvailabilityBar date={selectedDate} />

                {/* Date Jump Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className="p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex flex-col items-center text-xs"
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(1);
                      setSelectedDate(d);
                      setCalendarOpen(false);
                    }}
                  >
                    1st
                  </button>
                  <button
                    className="p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex flex-col items-center text-xs"
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(15);
                      setSelectedDate(d);
                      setCalendarOpen(false);
                    }}
                  >
                    15th
                  </button>
                  <button
                    className="p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex flex-col items-center text-xs"
                    onClick={() => {
                      const d = new Date(selectedDate);
                      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                      setSelectedDate(lastDay);
                      setCalendarOpen(false);
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}

        <div className="flex items-center justify-between text-sm ps-2 pe-2">
          <span className="flex items-center gap-1 text-text-light">
            <Clock className="w-4 h-4" />
            {filterTodayNonCanceledLessons().reduce((sum, lesson) => sum + parseInt(lesson.duration), 0)}min total
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${filterTodayLessons().length > 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
            {filterTodayNonCanceledLessons().length} lessons
          </span>
        </div>
      </div>

      {/* Lessons List */}
      <div className="space-y-3">
        {filterTodayLessons().length === 0 ? (
          <div className="card p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-text-light">No lessons today</h3>
            <button
              className="btn btn-primary flex items-center justify-center gap-1 mx-auto"
              onClick={() => navigate('/lesson/create')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Lesson
            </button>
          </div>
        ) : (
          filterTodayLessons().map((lesson) => (
            <div
              key={lesson.id}
              className="card p-4 hover:shadow-lg transition-all border-l-4 border-primary"
            >
              {/* Time & Status */}
              <div
                className="flex items-start justify-between mb-3"
                onClick={() => navigate(`/lesson/${lesson.id}`)}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xl font-bold min-w-[4rem] ${
                      lesson.status === 'canceled'
                        ? 'text-muted '
                        : 'text-primary '
                    }`}
                  >
                    {
                      new Date(lesson.time).toDateString() === new Date().toDateString()
                        ? 'Today,' + ` ${new Date(lesson.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : lesson.time.slice(0, -3)
                    }
                  </span>
                  <span className={`flex gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(lesson.status)}`}>
                    {getStatusIcon(lesson.status)}
                    {lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}
                  </span>
                </div>
                <span className="text-xs text-text-light font-medium">
                  {lesson.duration}
                </span>
              </div>

              <div className="flex justify-between">
                {/* Student & Location */}
                <div
                  className="space-y-1 mb-3"
                  onClick={() => navigate(`/lesson/${lesson.id}`)}
                >
                  <div className="flex items-center gap-2">
                    {!state.students.find((s) => s.id === lesson.student_id)?.picture && (<>
                      <User className="w-4 h-4 text-text-light flex-shrink-0" />
                    </>)}
                    {state.students.find((s) => s.id === lesson.student_id)?.picture && (<>
                      <div className="w-5 rounded-xl bg-blue-100">
                        <img src={state.students.find((s) => s.id === lesson.student_id)?.picture}/>
                      </div>
                    </>)}
                    {['teacher', 'parent'].includes(state.userRole || '') ? (<>
                    <span className="font-semibold">
                        {
                          state.students.find((s) => s.id === lesson.student_id)?.first_name
                        } {
                          state.students.find((s) => s.id === lesson.student_id)?.last_name
                        }
                      </span>
                    </>) : (<>
                      <span className="font-semibold">
                        {
                          state.teachers.find((s) => s.id === lesson.teacher_id)?.first_name
                        } {
                          state.teachers.find((s) => s.id === lesson.teacher_id)?.last_name
                        }
                      </span>
                    </>)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-light">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{lesson.location}</span>
                  </div>
                </div>

                <div className="dropdown inline-block relative">
                  <button
                    className="btn btn-secondary dropdown-toggle text-sm h-10 flex items-center justify-between gap-2"
                    onClick={() => setOpenDropdownId(openDropdownId === lesson.id ? null : lesson.id)}
                    aria-expanded={openDropdownId === lesson.id}
                  >
                    Actions
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${openDropdownId === lesson.id ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {openDropdownId === lesson.id && (
                    <div className="dropdown-menu absolute bottom-full left-0 z-50 w-full rounded-xl border border-border bg-white dark:bg-gray-800 shadow-2xl py-1.5">
                      <a
                        className="dropdown-item block px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleAction(lesson.id, 'no-show')}
                      >
                        Mark No Show
                      </a>
                      <a
                        className="dropdown-item block px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleAction(lesson.id, 'reschedule')}
                      >
                        Reschedule
                      </a>
                      <a
                        className="dropdown-item block px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => {
                          navigate(`/lesson/${lesson.id}`);
                          setOpenDropdownId(null);
                        }}
                      >
                        Edit Notes
                      </a>
                      <a
                        className="dropdown-item block px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                        onClick={() => handleAction(lesson.id, 'canceled')}
                      >
                        Cancel Event
                      </a>
                    </div>
                  )}
                </div>


              </div>

              {/* Notes */}
              {lesson.notes && (
                <p className="text-sm text-text-light bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
                  {lesson.notes}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LessonsList;
